/**
 * appDetails.ts
 * Static data for the per-app detail pages (/applications/:id).
 * Icons are referenced by lucide-react name (string) and resolved in AppDetail.tsx.
 * All content is derived from the Wanzo Academy curriculum — no secrets exposed.
 */

export interface AppFeature {
  iconName: string;
  title: string;
  desc: string;
}

export interface AppBadge {
  label: string;
  color: string; // tailwind text-* class
  bg: string;    // tailwind bg-* class
}

export interface AppDetailData {
  id: string;
  name: string;
  tagline: string;
  heroGradient: string;
  iconBg: string;
  iconName: string;
  category: 'PME' | 'Institution Financière' | 'Agents IA' | 'Téléchargeable';
  videoId?: string;       // YouTube video ID, leave undefined for placeholder
  badges?: AppBadge[];
  highlights?: string[];  // short bullet points shown in hero
  features: AppFeature[];
}

// ─── App registry ─────────────────────────────────────────────────────────────

const appDetailData: Record<string, AppDetailData> = {

  // ──────────────────────────────────────────────────────────────────────────
  // WANZO COMPTABILITÉ — Le Cerveau
  // ──────────────────────────────────────────────────────────────────────────
  accounting: {
    id: 'accounting',
    name: 'Comptabilité',
    tagline:
      'Le cerveau financier de votre PME : rigueur comptable, conformité SYSCOHADA et préparation automatique au crédit.',
    heroGradient: 'from-primary-900 via-primary-800 to-primary-700',
    iconBg: 'bg-primary',
    iconName: 'BarChart2',
    category: 'PME',
    videoId: undefined,
    badges: [
      { label: 'SYSCOHADA', color: 'text-primary-700', bg: 'bg-primary-100' },
      { label: 'IFRS',      color: 'text-primary-700', bg: 'bg-primary-100' },
      { label: 'FEC',       color: 'text-primary-700', bg: 'bg-primary-100' },
      { label: 'Blockchain',color: 'text-gray-700',   bg: 'bg-gray-100' },
    ],
    highlights: [
      'Import FEC (.xlsx / .csv / .json) pour migrer votre historique comptable',
      'Création et gestion des utilisateurs avec rôles et succursales',
      'Clôture et états financiers automatiques',
      "Score de crédit amélioré par votre historique transactionnel",
    ],
    features: [
      {
        iconName: 'Building2',
        title: 'Administration & rôles',
        desc:
          "Créez les utilisateurs (comptable, agent commercial, gérant de succursale), définissez les rôles et affectez chaque collaborateur à son point de vente depuis un seul tableau de bord.",
      },
      {
        iconName: 'Settings',
        title: 'Paramétrage expert',
        desc:
          "Configurez les exercices comptables, le plan comptable personnalisé, les devises, les taux de change et les règles de partage de données entre entités.",
      },
      {
        iconName: 'Upload',
        title: 'Import FEC (historique)',
        desc:
          "Importez jusqu'à 3 ans d'écritures passées via le format normalisé FEC (.xlsx, .csv, .json) pour rendre votre entreprise « Investment & Audit-Ready » dès le premier jour.",
      },
      {
        iconName: 'FileText',
        title: 'Gestion du journal',
        desc:
          "Saisie, validation et lettrage des écritures (achats, ventes, opérations diverses). Chaque entrée est horodatée et certifiée sur la chaîne Blockchain Wanzo.",
      },
      {
        iconName: 'BookOpen',
        title: 'Grand livre & comptes',
        desc:
          "Consultez et révisez les comptes, pointez les soldes et gérez la balance âgée clients/fournisseurs. Alertes automatiques sur les impayés.",
      },
      {
        iconName: 'PieChart',
        title: 'États financiers',
        desc:
          "Générez automatiquement le Bilan, le Compte de Résultat et le Tableau de Flux de Trésorerie selon les normes SYSCOHADA et IFRS en un clic.",
      },
      {
        iconName: 'BarChart3',
        title: 'Tableaux de bord',
        desc:
          "Analysez la marge, la rentabilité et les indicateurs clés (KPIs) sur des tableaux de bord interactifs. Visualisez les tendances et exportez les rapports.",
      },
      {
        iconName: 'Globe',
        title: 'Multi-devises',
        desc:
          "Gérez les transactions en CDF, USD, EUR et autres devises locales avec conversion automatique selon les taux configurés.",
      },
      {
        iconName: 'Lock',
        title: 'Clôture & intégrité',
        desc:
          "Verrouillez les exercices clôturés pour garantir l'immuabilité des données. Piste d'audit complète et traçabilité Blockchain pour toute vérification externe.",
      },
      {
        iconName: 'Zap',
        title: 'Assistant Adha IA',
        desc:
          "Adha analyse votre base comptable, automatise les écritures récurrentes et vous conseille en temps réel sur les actions à entreprendre pour améliorer votre score de crédit.",
      },
      {
        iconName: 'TrendingUp',
        title: 'Score de crédit',
        desc:
          "Votre historique comptable structure automatiquement votre Coffre-Fort de Crédibilité Wanzo. Les 5 piliers (Liquidité, Solvabilité, Stabilité, Activité, Intégrité) sont calculés en continu.",
      },
      {
        iconName: 'Users',
        title: 'Gestion des succursales',
        desc:
          "Créez autant de succursales et de points de vente que nécessaire. Consolidez les données ou analysez chaque entité de façon indépendante.",
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  // WANZO PRODUCTION — L'Atelier
  // ──────────────────────────────────────────────────────────────────────────
  production: {
    id: 'production',
    name: 'Production',
    tagline:
      'Votre atelier de bout en bout, de l\'achat des matières au produit fini, avec le vrai coût de chaque lot.',
    heroGradient: 'from-primary-900 via-primary-800 to-primary-700',
    iconBg: 'bg-primary',
    iconName: 'Factory',
    category: 'PME',
    videoId: undefined,
    badges: [
      { label: 'Coût de revient', color: 'text-primary-700', bg: 'bg-primary-100' },
      { label: 'Lots', color: 'text-primary-700', bg: 'bg-primary-100' },
      { label: 'IoT', color: 'text-gray-700', bg: 'bg-gray-100' },
      { label: 'SYSCOHADA', color: 'text-primary-700', bg: 'bg-primary-100' },
    ],
    highlights: [
      'Suivez chaque lot, de la matière première au produit fini',
      'Voyez ce que chaque produit vous coûte vraiment',
      'Approvisionnement, fournisseurs et réceptions au même endroit',
      'La comptabilité se met à jour toute seule à chaque production',
    ],
    features: [
      {
        iconName: 'Package',
        title: 'Matières et produits',
        desc:
          "Séparez clairement vos matières premières de vos produits finis. Chaque article a sa fiche, son unité et son coût.",
      },
      {
        iconName: 'Boxes',
        title: 'Composition et recettes',
        desc:
          "Définissez la recette de chaque produit : les intrants, le dosage et le coût matière se calculent tout seuls.",
      },
      {
        iconName: 'ShoppingCart',
        title: 'Approvisionnement',
        desc:
          "Passez vos commandes fournisseurs, réceptionnez les matières et suivez vos achats et vos crédits, sans quitter l'app.",
      },
      {
        iconName: 'Factory',
        title: 'Postes et gammes',
        desc:
          "Décrivez vos postes de travail et les étapes de fabrication. Les machines et les personnes sont rattachées à chaque étape.",
      },
      {
        iconName: 'Layers',
        title: 'Lots et traçabilité',
        desc:
          "Chaque déclaration de production crée un lot tracé. Vous savez toujours combien vous avez produit et à quel coût.",
      },
      {
        iconName: 'Bolt',
        title: 'Énergie et main d\'œuvre',
        desc:
          "L'énergie des machines et le temps de travail sont calculés automatiquement par lot, à partir de vos postes.",
      },
      {
        iconName: 'TrendingUp',
        title: 'Coût de revient',
        desc:
          "Matière, énergie, main d'œuvre : le coût de revient de chaque produit se construit tout seul, et son évolution est suivie dans le temps.",
      },
      {
        iconName: 'Wrench',
        title: 'Maintenance',
        desc:
          "Planifiez l'entretien de vos machines sur un calendrier, suivez les coûts et gardez vos équipements en état.",
      },
      {
        iconName: 'Cpu',
        title: 'Capteurs et IoT',
        desc:
          "Connectez vos machines et vos capteurs pour remonter automatiquement les données de production et de consommation.",
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  // WANZO ASSET — Les Lieux et les Biens
  // ──────────────────────────────────────────────────────────────────────────
  assets: {
    id: 'assets',
    name: 'Wanzo Asset',
    tagline:
      'Vos lieux et vos biens réunis : hôtels, salles de fête et loisirs, plus le suivi de vos actifs et de leur valeur.',
    heroGradient: 'from-primary-900 via-primary-800 to-primary-700',
    iconBg: 'bg-primary',
    iconName: 'Building2',
    category: 'PME',
    videoId: undefined,
    badges: [
      { label: 'Hôtellerie', color: 'text-primary-700', bg: 'bg-primary-100' },
      { label: 'Événements', color: 'text-primary-700', bg: 'bg-primary-100' },
      { label: 'Actifs', color: 'text-primary-700', bg: 'bg-primary-100' },
      { label: 'Charroi', color: 'text-gray-700', bg: 'bg-gray-100' },
    ],
    highlights: [
      'Réservez chambres, salles et espaces de loisir en un coup d\'œil',
      'Suivez la valeur et l\'entretien de vos biens',
      'Gérez votre charroi et sa logistique',
      'Le tout relié à votre comptabilité',
    ],
    features: [
      {
        iconName: 'BedDouble',
        title: 'Hôtellerie',
        desc:
          "Gérez vos chambres, les arrivées et les départs, et gardez une vue claire sur les disponibilités.",
      },
      {
        iconName: 'PartyPopper',
        title: 'Salles et événements',
        desc:
          "Réservez vos salles de réunion, de conférence ou de fête, et suivez les acomptes et les paiements.",
      },
      {
        iconName: 'Gamepad2',
        title: 'Espaces de loisir',
        desc:
          "Pilotez vos espaces de jeux et de loisir : accès, tarifs et fréquentation, simplement.",
      },
      {
        iconName: 'CalendarCheck',
        title: 'Calendrier des réservations',
        desc:
          "Toutes vos réservations sur un calendrier lisible, pour ne plus jamais surbooker un espace.",
      },
      {
        iconName: 'Boxes',
        title: 'Gestion des actifs',
        desc:
          "Tenez l'inventaire de vos immobilisations et de vos équipements, avec leur état et leur emplacement.",
      },
      {
        iconName: 'TrendingUp',
        title: 'Valeur et amortissement',
        desc:
          "Suivez la valeur de vos biens dans le temps et leur amortissement, prêt pour votre comptabilité.",
      },
      {
        iconName: 'Wrench',
        title: 'Maintenance des biens',
        desc:
          "Planifiez l'entretien de vos équipements et de vos locaux, et gardez l'historique des interventions.",
      },
      {
        iconName: 'Truck',
        title: 'Charroi et logistique',
        desc:
          "Gérez vos véhicules, leurs entretiens et leurs affectations, pour une logistique maîtrisée.",
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  // WANZO GESTION COMMERCIALE — Le Terrain
  // ──────────────────────────────────────────────────────────────────────────
  business: {
    id: 'business',
    name: 'Gestion commerciale',
    tagline:
      'Le terrain digitalisé : POS mobile, caisse, stocks et clients. Chaque vente devient une donnée certifiée.',
    heroGradient: 'from-primary-900 via-primary-800 to-primary-700',
    iconBg: 'bg-primary-700',
    iconName: 'Briefcase',
    category: 'PME',
    videoId: undefined,
    badges: [
      { label: 'Mobile & Desktop', color: 'text-primary-700', bg: 'bg-primary-100' },
      { label: 'POS',             color: 'text-primary-700', bg: 'bg-primary-100' },
      { label: 'Hors-ligne',      color: 'text-gray-700',  bg: 'bg-gray-100' },
    ],
    highlights: [
      'POS (Point of Sale) sur mobile et desktop avec scan code-barres/QR',
      'Caisse intégrée synchronisée avec la comptabilité',
      'Catalogue produits avec variantes et affectation par point de vente',
      'Gestion clients et fournisseurs unifiée',
    ],
    features: [
      {
        iconName: 'Building2',
        title: 'Configuration Business Unit',
        desc:
          "Paramétrez la structure de votre entreprise par niveau : Entreprise, Succursale, Point de vente. Chaque niveau dispose de ses propres tableaux de bord et droits d'accès.",
      },
      {
        iconName: 'ShoppingCart',
        title: 'POS & facturation',
        desc:
          "Interface caissier intuitive, disponible sur mobile et desktop. Scan code-barres et QR code, paiement cash ou Mobile Money, impression ou envoi de reçu digital.",
      },
      {
        iconName: 'DollarSign',
        title: 'Gestion de caisse',
        desc:
          "Ouverture et clôture de caisse, fond de caisse, reconciliation quotidienne automatique avec le journal comptable. Alertes sur les écarts.",
      },
      {
        iconName: 'Package',
        title: 'Stocks & catalogue',
        desc:
          "Ajoutez produits et variantes (taille, couleur, etc.), configurez les prix et les affectations par point de vente. Alertes de rupture de stock automatiques.",
      },
      {
        iconName: 'TrendingDown',
        title: 'Seuils de réapprovisionnement',
        desc:
          "Définissez les seuils min/max par article et recevez des alertes ou des commandes automatiques dès qu'un seuil critique est atteint.",
      },
      {
        iconName: 'Users',
        title: 'Gestion des tiers (clients)',
        desc:
          "Fiches clients complètes : coordonnées, historique des achats, soldes et relances. Segmentation et analyse de la valeur client (RFM).",
      },
      {
        iconName: 'Truck',
        title: 'Gestion des fournisseurs',
        desc:
          "Suivi des commandes fournisseurs, réception des marchandises, gestion des factures d'achat et des délais de paiement.",
      },
      {
        iconName: 'BarChart3',
        title: 'Pipeline & performance',
        desc:
          "Tableaux de bord des ventes par produit, par vendeur, par succursale. Prévisions et comparaisons période par période.",
      },
      {
        iconName: 'Globe',
        title: 'Mode hors-ligne',
        desc:
          "Continuez à vendre même sans connexion. Les données sont synchronisées automatiquement dès le retour du réseau.",
      },
      {
        iconName: 'Zap',
        title: 'Assistant Commercial IA',
        desc:
          "L'assistant IA analyse vos données de vente, identifie les opportunités, suggère des actions commerciales et automatise les relances clients.",
      },
      {
        iconName: 'FileText',
        title: 'Devis & factures',
        desc:
          "Créez des devis professionnels et convertissez-les en factures d'un tap. Envoi par email ou WhatsApp, suivi du statut en temps réel.",
      },
      {
        iconName: 'Lock',
        title: 'Traçabilité certifiée',
        desc:
          "Chaque transaction est horodatée et certifiée dans le Coffre-Fort Wanzo. Vos ventes constituent une preuve irréfutable pour les institutions de crédit.",
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  // WANZO AVEC : la réunion de l'AVEC sur téléphone
  // ──────────────────────────────────────────────────────────────────────────
  avec: {
    id: 'avec',
    name: 'Wanzo AVEC',
    tagline:
      "Le carnet de votre AVEC sur téléphone : la réunion guide l'application, membre par membre, comme sur papier.",
    heroGradient: 'from-primary-900 via-primary-800 to-primary-700',
    iconBg: 'bg-primary',
    iconName: 'Users',
    category: 'Téléchargeable',
    videoId: undefined,
    badges: [
      { label: 'Android', color: 'text-primary-700', bg: 'bg-primary-100' },
      { label: 'Hors ligne', color: 'text-gray-700', bg: 'bg-gray-100' },
      { label: 'FC et $', color: 'text-primary-700', bg: 'bg-primary-100' },
      { label: 'Français, Swahili, Lingala', color: 'text-gray-700', bg: 'bg-gray-100' },
    ],
    highlights: [
      'Réunion pas à pas : parts, solidarité, amendes, remboursements et crédits',
      'Caisse comptée en fin de réunion, PV signé par code PIN',
      'Carnet de chaque membre en PDF, à imprimer ou partager sur WhatsApp',
      "Fonctionne sans réseau, tout se synchronise au retour de la connexion",
    ],
    features: [
      {
        iconName: 'CalendarCheck',
        title: 'Réunions guidées',
        desc:
          "On ouvre la réunion, on passe membre par membre (présence, parts, solidarité, amendes, remboursements), on compte la caisse et on ferme. Une réunion fermée ne se modifie plus : toute correction est tracée.",
      },
      {
        iconName: 'Users',
        title: 'Membres et dirigeants',
        desc:
          "Fiche de chaque membre avec photo et pièce d'identité (carte d'électeur, passeport, permis et autres pièces valables en RDC). Président, secrétaire et trésorier ont chacun leur rôle.",
      },
      {
        iconName: 'DollarSign',
        title: 'Épargne et parts',
        desc:
          "Valeur de la part fixée par le règlement de l'AVEC, achat de parts à chaque réunion et suivi de l'épargne de chacun, en francs congolais ou en dollars.",
      },
      {
        iconName: 'CreditCard',
        title: 'Crédits aux membres',
        desc:
          "Octroi dans les plafonds du règlement, intérêt calculé sur le restant dû, garant facultatif, échéances et retards suivis automatiquement.",
      },
      {
        iconName: 'Shield',
        title: 'Caisse de solidarité',
        desc:
          "Cotisations et aides accordées aux membres, avec le solde toujours visible. L'aide ne dépasse jamais ce que la caisse contient.",
      },
      {
        iconName: 'TrendingUp',
        title: 'Activités génératrices de revenus (AGR)',
        desc:
          "Recettes, dépenses et résultat de chaque AGR, partagés selon la règle fixée par l'AVEC. Une AGR de commerce ouvre le mode point de vente.",
      },
      {
        iconName: 'PieChart',
        title: 'Partage de fin de cycle',
        desc:
          "Calcul du partage membre par membre en fin de cycle, devise par devise, avec un aperçu avant validation.",
      },
      {
        iconName: 'FileText',
        title: 'Carnets, PV et états',
        desc:
          "Carnet du membre, procès-verbal de réunion, registre des opérations, registre des crédits et états de caisse en PDF, à imprimer ou partager.",
      },
      {
        iconName: 'Lock',
        title: 'Validation par code PIN',
        desc:
          "Les décisions importantes se valident par code PIN, sur le téléphone du secrétaire ou à distance, comme les trois clés de la caisse.",
      },
      {
        iconName: 'Globe',
        title: 'Sans réseau',
        desc:
          "L'application fonctionne hors ligne pendant la réunion. Les opérations sont envoyées dès que la connexion revient, sans doublon.",
      },
      {
        iconName: 'Building2',
        title: "Structure d'appui",
        desc:
          "L'AVEC est suivie par son programme d'appui (ou par le Réseau ikiotahub par défaut), qui voit son évolution sans jamais accéder aux données sensibles des membres.",
      },
      {
        iconName: 'BookOpen',
        title: 'Simple et en trois langues',
        desc:
          "Pensée pour les dirigeants d'AVEC, en ville comme au village : grandes touches, messages courts, en français, swahili et lingala.",
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  // WANZO WALLET — Le Coffre-Fort
  // ──────────────────────────────────────────────────────────────────────────
  wallet: {
    id: 'wallet',
    name: 'Wallet',
    tagline:
      "Le Coffre-Fort financier de votre PME : Mobile Money centralisé, demandes de crédit et levier pour le financement.",
    heroGradient: 'from-primary-800 via-primary-700 to-primary-600',
    iconBg: 'bg-primary-600',
    iconName: 'DollarSign',
    category: 'PME',
    videoId: undefined,
    badges: [
      { label: 'Airtel Money',    color: 'text-primary-700', bg: 'bg-primary-100' },
      { label: 'M-Pesa',          color: 'text-primary-700', bg: 'bg-primary-100' },
      { label: 'Orange Money',    color: 'text-primary-700', bg: 'bg-primary-100' },
      { label: 'Afrimoney',       color: 'text-primary-700', bg: 'bg-primary-100' },
    ],
    highlights: [
      "Porte d'entrée unique pour les demandes de crédit, leasing et avance sur facture",
      'Dépôts et retraits via tous les opérateurs Mobile Money',
      'Prélèvement automatique pour les remboursements',
      'Score de crédit visible et actionnable depuis le Wallet',
    ],
    features: [
      {
        iconName: 'CreditCard',
        title: 'Dépôts & retraits',
        desc:
          "Gérez vos entrées et sorties de fonds via Airtel Money, M-Pesa, Orange Money ou Afrimoney. Confirmation instantanée, historique complet.",
      },
      {
        iconName: 'Globe',
        title: 'Multi-opérateurs',
        desc:
          "Compatible avec tous les grands opérateurs Mobile Money actifs en RDC et en Afrique centrale. No single point of failure.",
      },
      {
        iconName: 'TrendingUp',
        title: 'Demande de crédit',
        desc:
          "Initiez une demande de crédit directement depuis le Wallet. Le système analyse votre score Wanzo et soumet le dossier aux institutions partenaires.",
      },
      {
        iconName: 'Package',
        title: 'Demande de leasing',
        desc:
          "Soumettez une demande de leasing pour équiper votre PME. Validation accélérée pour les entreprises avec un score Wanzo élevé.",
      },
      {
        iconName: 'FileText',
        title: 'Avance sur facture',
        desc:
          "Cédez vos créances clients et obtenez une avance de trésorerie en 48h. Résoudre les problèmes de trésorerie sans attendre le règlement des clients.",
      },
      {
        iconName: 'BarChart3',
        title: 'Score de crédit en direct',
        desc:
          "Visualisez vos 5 piliers de score (Liquidité, Solvabilité, Stabilité, Activité, Intégrité) et comprenez chaque levier pour l'améliorer.",
      },
      {
        iconName: 'Bell',
        title: 'Alertes & notifications',
        desc:
          "Notifications en temps réel pour chaque transaction, chaque remboursement dû et chaque mise à jour de votre score.",
      },
      {
        iconName: 'Lock',
        title: 'Prélèvement automatique',
        desc:
          "Configurez le prélèvement automatique via Smart Contract pour honorer vos remboursements sans action manuelle. Chaque échéance améliore votre score.",
      },
      {
        iconName: 'Shield',
        title: 'Sécurité renforcée',
        desc:
          "Authentification à deux facteurs, chiffrement de bout en bout et traçabilité Blockchain pour tous les mouvements de fonds.",
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  // PROJET
  // ──────────────────────────────────────────────────────────────────────────
  project: {
    id: 'project',
    name: 'Projet',
    tagline:
      'Planifiez, suivez et collaborez sur tous vos projets, connecté à votre comptabilité.',
    heroGradient: 'from-primary-900 via-primary-800 to-primary-700',
    iconBg: 'bg-primary-700',
    iconName: 'Layers',
    category: 'PME',
    videoId: undefined,
    badges: [
      { label: 'Kanban',    color: 'text-primary-700', bg: 'bg-primary-100' },
      { label: 'Gantt',     color: 'text-primary-700', bg: 'bg-primary-100' },
      { label: 'Collaboratif', color: 'text-primary-700', bg: 'bg-primary-100' },
    ],
    highlights: [
      'Vues Kanban, liste et Gantt selon vos préférences',
      'Assignation, commentaires et partage de fichiers',
      'Lien direct avec le suivi budgétaire comptable',
      'Multi-projets depuis un seul espace de travail',
    ],
    features: [
      {
        iconName: 'Layers',
        title: 'Gestion des tâches',
        desc:
          "Kanban, liste et Gantt disponibles pour tous les types de projets. Glissez-déposez les tâches entre les colonnes, définissez les priorités et les échéances.",
      },
      {
        iconName: 'Users',
        title: 'Collaboration équipe',
        desc:
          "Assignez des tâches, commentez directement sur les cartes, joignez des fichiers et mentionnez vos collaborateurs. Tout le monde est aligné.",
      },
      {
        iconName: 'Bell',
        title: 'Jalons & rappels',
        desc:
          "Définissez les jalons clés de votre projet et recevez des alertes automatiques lorsque les échéances approchent ou sont dépassées.",
      },
      {
        iconName: 'FileText',
        title: "Rapports d'avancement",
        desc:
          "Tableaux de bord temps réel sur le taux de complétion, les tâches en retard et les KPIs du projet. Export PDF pour les parties prenantes.",
      },
      {
        iconName: 'Globe',
        title: 'Multi-projets',
        desc:
          "Gérez plusieurs projets simultanément depuis un seul espace de travail. Vue portefeuille pour avoir une vision globale de tous les chantiers.",
      },
      {
        iconName: 'Lock',
        title: "Droits d'accès",
        desc:
          "Contrôlez finement qui voit et modifie quoi dans chaque projet. Espaces privés, partagés ou publics selon les besoins.",
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  // TÉLÉSURVEILLANCE
  // ──────────────────────────────────────────────────────────────────────────
  telesurveillance: {
    id: 'telesurveillance',
    name: 'Télésurveillance',
    tagline:
      'Sécurisez vos locaux commerciaux et protégez votre outil de travail à distance.',
    heroGradient: 'from-primary-900 via-primary-800 to-primary-700',
    iconBg: 'bg-primary-800',
    iconName: 'Camera',
    category: 'PME',
    videoId: undefined,
    badges: [
      { label: 'Cloud',    color: 'text-primary-700', bg: 'bg-primary-100' },
      { label: 'Mobile',   color: 'text-primary-700', bg: 'bg-primary-100' },
      { label: 'Alertes',  color: 'text-gray-600',   bg: 'bg-gray-100' },
    ],
    highlights: [
      "Vidéo en direct accessible sur mobile depuis n'importe où",
      'Détection de mouvement avec alertes instantanées',
      'Enregistrement cloud sécurisé pendant 30 jours',
      'Surveillance multi-sites depuis une interface unifiée',
    ],
    features: [
      {
        iconName: 'Camera',
        title: 'Vidéo en direct',
        desc:
          "Flux live depuis toutes vos caméras accessibles sur mobile ou desktop. Qualité HD, faible latence et visualisation multi-caméras simultanées.",
      },
      {
        iconName: 'AlertTriangle',
        title: 'Alertes intrusion',
        desc:
          "Détection de mouvement configurable avec notification push immédiate. Définissez des zones de détection et des plages horaires.",
      },
      {
        iconName: 'FileText',
        title: 'Enregistrement cloud',
        desc:
          "Stockage sécurisé des séquences vidéo pendant 30 jours. Accès aux archives depuis l'application et export des clips pour les assurances ou la police.",
      },
      {
        iconName: 'Bell',
        title: 'Tableau de bord sécurité',
        desc:
          "Vue d'ensemble de l'état de tous vos capteurs et caméras. Historique des événements et des alertes déclenchées.",
      },
      {
        iconName: 'Users',
        title: 'Multi-sites',
        desc:
          "Surveillez plusieurs locaux, succursales ou entrepôts depuis une seule interface. Groupes de caméras par site.",
      },
      {
        iconName: 'Lock',
        title: 'Accès sécurisé',
        desc:
          "Authentification forte pour l'accès aux flux vidéo. Journaux d'accès complets et alertes en cas de tentative d'accès non autorisé.",
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  // GESTION DE PORTEFEUILLE — Institution Financière
  // ──────────────────────────────────────────────────────────────────────────
  portfolio: {
    id: 'portfolio',
    name: 'Gestion de Portefeuille',
    tagline:
      'Pilotez votre portefeuille de crédits avec les données financières et non-financières des PME Wanzo.',
    heroGradient: 'from-primary-900 via-primary-800 to-primary-700',
    iconBg: 'bg-primary-700',
    iconName: 'TrendingUp',
    category: 'Institution Financière',
    videoId: undefined,
    badges: [
      { label: 'COBAC',     color: 'text-primary-700', bg: 'bg-primary-100' },
      { label: 'BCEAO',     color: 'text-primary-700', bg: 'bg-primary-100' },
      { label: 'MLOps',     color: 'text-gray-600',   bg: 'bg-gray-100' },
      { label: 'Blockchain',color: 'text-gray-700',   bg: 'bg-gray-100' },
    ],
    highlights: [
      'Alternative Credit Scoring basé sur 5 piliers (liquidité, solvabilité, stabilité, activité, intégrité)',
      'Données financières ET non-financières (Mobile Money, géolocalisation, etc.)',
      'Reporting réglementaire automatique (COBAC, BCC, BCEAO)',
      'Sourcing digital et due diligence des PME via écosystème Wanzo',
    ],
    features: [
      {
        iconName: 'BarChart3',
        title: 'Tableau de bord portefeuille',
        desc:
          "Vision globale des encours, taux de retard (PAR), provisions et performances. Segmentation par secteur, région, taille d'entreprise et classe de risque.",
      },
      {
        iconName: 'PieChart',
        title: 'Analyse de concentration',
        desc:
          "Mesurez et gérez la concentration du portefeuille par secteur et par contrepartie. Alertes automatiques en cas de dépassement des limites réglementaires.",
      },
      {
        iconName: 'Search',
        title: 'Alternative Credit Scoring (ACS)',
        desc:
          "Le modèle ACS Wanzo intègre les 5 piliers (Liquidité, Solvabilité, Stabilité, Activité, Intégrité) et des données non-financières pour évaluer même les PME sans historique bancaire.",
      },
      {
        iconName: 'Globe',
        title: 'Données non-financières',
        desc:
          "Collecte et analyse : consommation d'énergie, données de géolocalisation, Mobile Money, IoT agritech/logtech, réputation locale et données de crédit internet.",
      },
      {
        iconName: 'AlertTriangle',
        title: 'Gestion des risques',
        desc:
          "Classement PAR automatique, alertes sur dossiers à risque, simulation de stress-test et calcul de provisions selon les standards prudentiels.",
      },
      {
        iconName: 'FileText',
        title: 'Reporting réglementaire',
        desc:
          "États COBAC, BCC et BCEAO générés automatiquement. Exportez en un clic les rapports de supervision dans les formats attendus.",
      },
      {
        iconName: 'Users',
        title: 'Sourcing digital & due diligence',
        desc:
          "Identifiez et évaluez les PME éligibles depuis l'écosystème Wanzo. Dossiers d'investissement pré-instruits avec données comptables certifiées.",
      },
      {
        iconName: 'Layers',
        title: 'Affacturage & leasing',
        desc:
          "Structurez et gérez les opérations d'affacturage et de leasing. Gestion des garanties, des sûretés et des fonds de garantie mutuelle.",
      },
      {
        iconName: 'Lock',
        title: 'Smart Contracts & MLOps',
        desc:
          "Automatisez les déblocages de fonds, les prélèvements de remboursement (via Wanzo Wallet) et la maintenance des modèles de scoring en production.",
      },
      {
        iconName: 'Shield',
        title: 'Data Forensic & Blockchain',
        desc:
          "Utilisez les logs Blockchain pour garantir l'immuabilité des preuves financières. Audit complet des historiques et détection de fraude documentaire.",
      },
      {
        iconName: 'TrendingUp',
        title: 'Suivi des performances',
        desc:
          "Mesurez le rendement du portefeuille en temps réel. Méthodologies adaptées aux marchés africains, incluant indicateurs non-standard.",
      },
      {
        iconName: 'Bell',
        title: 'Alertes & workflows',
        desc:
          "Circuits de validation configurables pour l'approbation des crédits, avec délégations de signature, notifications et suivi du SLA.",
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  // PROSPECTION & ANALYSE DES RISQUES
  // ──────────────────────────────────────────────────────────────────────────
  risk: {
    id: 'risk',
    name: 'Prospection & Analyse des risques',
    tagline:
      "Évaluez les dossiers en quelques minutes grâce au scoring IA et à l'analyse forensique des données.",
    heroGradient: 'from-primary-900 via-primary-800 to-primary-700',
    iconBg: 'bg-primary-700',
    iconName: 'BarChart2',
    category: 'Institution Financière',
    videoId: undefined,
    badges: [
      { label: 'Machine Learning', color: 'text-gray-600',   bg: 'bg-gray-100' },
      { label: 'BÂLE III',         color: 'text-gray-700',   bg: 'bg-gray-100' },
      { label: 'No-code',          color: 'text-gray-600',   bg: 'bg-gray-100' },
    ],
    highlights: [
      "Modèles ML pour le scoring automatique des demandes de crédit",
      "Détection d'anomalies documentaires par IA",
      "Intégration de données non-financières (Mobile Money, IoT, etc.)",
      "Workflow d'approbation configurable avec signature électronique",
    ],
    features: [
      {
        iconName: 'Search',
        title: 'Moteur de scoring IA',
        desc:
          "Modèles de Machine Learning entraînés sur les données transactionnelles Wanzo pour évaluer automatiquement les demandes de crédit en quelques secondes.",
      },
      {
        iconName: 'FileText',
        title: 'Analyse financière automatisée',
        desc:
          "Calcul automatique des ratios financiers (liquidité, solvabilité, rentabilité), tendances et comparaisons sectorielles depuis les données FEC importées.",
      },
      {
        iconName: 'AlertTriangle',
        title: "Détection d'anomalies",
        desc:
          "Identification des incohérences dans les documents soumis (bilans, extraits bancaires, déclarations fiscales). Alertes fraud et signaux faibles.",
      },
      {
        iconName: 'Globe',
        title: 'Données alternatives',
        desc:
          "Collecte et normalisation des sources alternatives : Mobile Money, consommation d'énergie, géolocalisation, IoT, réseaux sociaux locaux.",
      },
      {
        iconName: 'BarChart3',
        title: 'Tableau de bord risque',
        desc:
          "Vue consolidée des expositions par classe d'actif, secteur et région. Courbes de distribution des scores et analyses de portefeuille.",
      },
      {
        iconName: 'Users',
        title: "Workflow d'approbation",
        desc:
          "Circuits de validation configurables avec délégations de pouvoirs, signatures électroniques et upload des pièces justificatives.",
      },
      {
        iconName: 'Lock',
        title: 'Conformité BÂLE & réglementaire',
        desc:
          "Calcul des fonds propres réglementaires, respect des exigences prudentielles COBAC/BCC et production des états de supervision.",
      },
      {
        iconName: 'TrendingUp',
        title: 'Feature Engineering',
        desc:
          "Transformation des flux transactionnels en variables prédictives pour les modèles de scoring. Mise à jour continue des features en production (MLOps).",
      },
    ],
  },

  // ──────────────────────────────────────────────────────────────────────────
  // ADHA CLAW — Agents IA Agentiques
  // ──────────────────────────────────────────────────────────────────────────
  'adha-claw': {
    id: 'adha-claw',
    name: 'Adha Claw',
    tagline:
      "Des agents IA spécialisés qui automatisent votre PME tâche par tâche (du contrôle de gestion au marketing digital).",
    heroGradient: 'from-primary-900 via-primary-800 to-primary-700',
    iconBg: 'bg-primary',
    iconName: 'Zap',
    category: 'Agents IA',
    videoId: undefined,
    badges: [
      { label: 'No-code',   color: 'text-gray-600',   bg: 'bg-gray-100' },
      { label: 'Low-code',  color: 'text-gray-600',   bg: 'bg-gray-100' },
      { label: 'Agentique', color: 'text-primary-700', bg: 'bg-primary-100' },
    ],
    highlights: [
      "Agents autonomes configurables pour chaque fonction de l'entreprise",
      'Contrôle de gestion automatisé : suivi budgétaire et analyse des écarts',
      'Marketing digital basé sur les données clients et la veille concurrentielle',
      'Intégration No-Code dans les flux Wanzo existants',
    ],
    features: [
      {
        iconName: 'FileText',
        title: 'Facturation automatique',
        desc:
          "Génère et envoie les factures sans intervention dès qu'une vente est validée dans le POS. Formats PDF et email configurables.",
      },
      {
        iconName: 'Bell',
        title: 'Relances clients automatiques',
        desc:
          "Surveille les impayés et déclenche automatiquement des relances selon les règles métier définies (J+7, J+15, J+30). Historique complet des relances.",
      },
      {
        iconName: 'Package',
        title: 'Réapprovisionnement intelligent',
        desc:
          "Analyse les niveaux de stock et commande automatiquement auprès des fournisseurs dès qu'un seuil minimal est atteint. Prédit la demande future.",
      },
      {
        iconName: 'BarChart3',
        title: 'Contrôle de gestion',
        desc:
          "Suivi budgétaire et analyse des écarts en continu. L'agent alerte automatiquement en cas de dépassement et propose des mesures correctives.",
      },
      {
        iconName: 'Globe',
        title: 'Marketing digital automatisé',
        desc:
          "Déploiement d'agents pour l'étude de marché, l'analyse de la concurrence et l'automatisation des campagnes basées sur les données clients Wanzo.",
      },
      {
        iconName: 'Users',
        title: 'Gestion RH légère',
        desc:
          "Collecte et traite les pointages, gère les absences et envoie les notifications sans intervention RH quotidienne. Intégration avec la paie.",
      },
      {
        iconName: 'FileText',
        title: 'Rapports hebdomadaires',
        desc:
          "Synthèses de gestion générées et envoyées chaque semaine dans votre boîte mail : ventes, trésorerie, stocks, indicateurs clés.",
      },
      {
        iconName: 'Shield',
        title: 'Veille réglementaire',
        desc:
          "L'agent surveille les évolutions réglementaires (fiscales, sociales, sectorielles) et vous alerte des changements impactant votre activité.",
      },
      {
        iconName: 'Zap',
        title: 'Agents configurables No-Code',
        desc:
          "Activez, désactivez et paramétrez chaque agent depuis une interface simple sans code. Créez des agents personnalisés pour des fonctions spécifiques (support client, veille, etc.).",
      },
      {
        iconName: 'TrendingUp',
        title: 'Amélioration du score de crédit',
        desc:
          "Adha Claw identifie les actions qui améliorent chacun des 5 piliers du score Wanzo et automatise leur exécution pour optimiser votre profil de crédit.",
      },
    ],
  },
};

export default appDetailData;
